/**
 * Navigation Module
 * Handles expanding/collapsing submenus on click
 * Handles menu toggle button
 */
const nav = document.querySelector('.site-header__nav');
const navToggle = document.getElementById('navToggle');
const navSearchToggle = document.getElementById('navSearchToggle');
const subMenus = nav.querySelectorAll('.sub-menu');
const isMobileNav = () => window.matchMedia('(max-width: 1100px)').matches;
const searchMenuItem = nav?.querySelector('.menu-item--search');
const searchSubmenu = searchMenuItem?.querySelector('.menu-search-submenu');
const searchClose = searchMenuItem?.querySelector('.search-close');
const searchFormElements = searchMenuItem?.querySelectorAll('input, button');

const setElementTabbable = (element, isTabbable) => {
	if (!element) {
		return;
	}

	if (isTabbable) {
		element.removeAttribute('tabindex');
	} else {
		element.setAttribute('tabindex', '-1');
	}
};

const updateAriaAttributes = () => {
	if (!nav) {
		return;
	}

	const isNavOpen = nav.classList.contains('nav-open');

	if (navToggle) {
		navToggle.setAttribute('aria-expanded', isNavOpen ? 'true' : 'false');
	}

	const menuItemsWithChildren = nav.querySelectorAll('.menu-item-has-children');
	menuItemsWithChildren.forEach((menuItem, index) => {
		const link = menuItem.querySelector('a');
		const submenu = menuItem.querySelector('.sub-menu');

		if (!link || !submenu) {
			return;
		}

		if (!submenu.id) {
			submenu.id = `submenu-${index + 1}`;
		}

		const isExpanded = menuItem.classList.contains('menu-item-expanded');
		link.setAttribute('aria-haspopup', 'true');
		link.setAttribute('aria-controls', submenu.id);
		link.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
	});
};

const updateTabbability = () => {
	if (!nav) {
		return;
	}

	const isNavOpen = nav.classList.contains('nav-open');
	const allowTopLevel = !isMobileNav() || isNavOpen;
	const topLevelLinks = nav.querySelectorAll('.menu > li > a');

	topLevelLinks.forEach(link => {
		const menuItem = link.closest('li');
		if (menuItem?.classList.contains('hamburger-menu') || menuItem?.classList.contains('menu-item--search')) {
			setElementTabbable(link, false);
			return;
		}

		setElementTabbable(link, allowTopLevel);
	});

	const menuItemsWithChildren = nav.querySelectorAll('.menu-item-has-children');
	menuItemsWithChildren.forEach(menuItem => {
		const isExpanded = menuItem.classList.contains('menu-item-expanded');
		const submenuLinks = menuItem.querySelectorAll('.sub-menu a');
		submenuLinks.forEach(link => {
			setElementTabbable(link, isExpanded && (!isMobileNav() || isNavOpen));
		});
	});

	if (searchFormElements?.length) {
		const isSearchExpanded = searchMenuItem?.classList.contains('menu-item-expanded');
		const isSearchVisible = isSearchExpanded || (isMobileNav() && isNavOpen);
		searchFormElements.forEach(element => {
			setElementTabbable(element, Boolean(isSearchVisible));
		});
	}

	updateAriaAttributes();
};

const setSubmenuHeight = (submenu) => {
	const height = submenu ? submenu.scrollHeight : 0;
	const paddedHeight = height > 0 ? height + 20 : 26;
	nav.style.setProperty('--submenu-height', `${paddedHeight}px`);
};

const setSubmenuMaxHeight = (submenu, isOpen) => {
	if (!submenu) return;
	if (isMobileNav()) {
		submenu.style.maxHeight = isOpen ? `${submenu.scrollHeight}px` : '0px';
		submenu.style.opacity = isOpen ? '1' : '0';
	}
};

const closeSearchMenu = () => {
	if (!searchMenuItem) {
		return;
	}

	searchMenuItem.classList.remove('menu-item-expanded');
	setSubmenuMaxHeight(searchSubmenu, false);
	if (navSearchToggle) {
		navSearchToggle.setAttribute('aria-expanded', 'false');
	}

	const expandedItem = nav.querySelector('.menu-item-expanded');
	const expandedSubmenu = expandedItem?.querySelector('.sub-menu');
	setSubmenuHeight(expandedSubmenu);
	updateTabbability();
};

const openSearchMenu = () => {
	if (!searchMenuItem || !searchSubmenu) {
		return;
	}

	const menuItemsWithChildren = nav.querySelectorAll('.menu-item-has-children');
	menuItemsWithChildren.forEach(item => {
		if (item === searchMenuItem) {
			return;
		}
		item.classList.remove('menu-item-expanded');
		setSubmenuMaxHeight(item.querySelector('.sub-menu'), false);
	});

	if (isMobileNav() && !nav.classList.contains('nav-open')) {
		nav.classList.add('nav-open');
	}

	if (!nav.classList.contains('nav-opening')) {
		nav.classList.add('nav-opening');
		setTimeout(() => {
			nav.classList.remove('nav-opening');
		}, 600);
	}

	searchMenuItem.classList.add('menu-item-expanded');
	setSubmenuHeight(searchSubmenu);
	setSubmenuMaxHeight(searchSubmenu, true);
	if (navSearchToggle) {
		navSearchToggle.setAttribute('aria-expanded', 'true');
	}

	updateTabbability();
	searchMenuItem.querySelector('.search-form__input')?.focus();
};

const toggleSearchMenu = () => {
	const isExpanded = searchMenuItem?.classList.contains('menu-item-expanded');
	if (isExpanded) {
		closeSearchMenu();
	} else {
		openSearchMenu();
	}
};

const openNav = () => {
	nav.classList.add('nav-open');
	nav.classList.add('nav-opening');
	// Expand the first menu item that has a submenu
	const menuItemsWithChildren = nav.querySelectorAll('.menu-item-has-children');
	if (menuItemsWithChildren.length > 0) {
		const firstMenuItemWithChildren = menuItemsWithChildren[0];
		firstMenuItemWithChildren.classList.add('menu-item-expanded');
		const submenu = firstMenuItemWithChildren.querySelector('.sub-menu');
		setSubmenuHeight(submenu);
		setSubmenuMaxHeight(submenu, true);
	}
	setTimeout(() => {
		nav.classList.remove('nav-opening');
	}, 600);
	updateTabbability();
}

const closeNav = () => {
	nav.classList.remove('nav-open');
	nav.classList.remove('nav-opening');
	const menuItemsWithChildren = nav.querySelectorAll('.menu-item-has-children');
	menuItemsWithChildren.forEach(item => {
		item.classList.remove('menu-item-expanded');
		setSubmenuMaxHeight(item.querySelector('.sub-menu'), false);
	});
	if (navSearchToggle) {
		navSearchToggle.setAttribute('aria-expanded', 'false');
	}
	setSubmenuHeight(null);
	updateTabbability();
}

const toggleNav = () => {
	const isOpen = nav.classList.contains('nav-open');
	const isHamburgerMenuExpanded = nav.querySelector('.hamburger-menu.menu-item-expanded');
	const isSearchMenuExpanded = searchMenuItem?.classList.contains('menu-item-expanded');

	if (!isMobileNav() && isSearchMenuExpanded) {
		closeSearchMenu();
	}

	if (isOpen && (isHamburgerMenuExpanded || isSearchMenuExpanded)) {
		closeNav();
	} else if (isOpen) {
		// Hamburger toggle clicked but another submenu is already open
		const menuItemsWithChildren = nav.querySelectorAll('.menu-item-has-children');
		menuItemsWithChildren.forEach(item => {
			item.classList.remove('menu-item-expanded');
		});

		openNav();

	} else {
		openNav();
	}
}

if (!nav) {
	// Exit if no navigation found
} else {
	// Find all menu items with children
	const menuItemsWithChildren = nav.querySelectorAll('.menu-item-has-children');

	if (menuItemsWithChildren.length > 0) {
		setSubmenuHeight(null);

		menuItemsWithChildren.forEach(menuItem => {
			const link = menuItem.querySelector('a');

			if (!link) {
				return;
			}

			// Add click handler to toggle submenu
			link.addEventListener('click', (e) => {
				if (isMobileNav()) {
					e.preventDefault();
				}

				if (!isMobileNav() && searchMenuItem?.classList.contains('menu-item-expanded')) {
					closeSearchMenu();
				}

				// Check if this menu item is already expanded
				const isExpanded = menuItem.classList.contains('menu-item-expanded');
				const nextSubmenu = menuItem.querySelector('.sub-menu');

				if (!isExpanded) {
					setSubmenuHeight(nextSubmenu);
					setSubmenuMaxHeight(nextSubmenu, true);
				}

				// Close all other expanded menus
				menuItemsWithChildren.forEach(item => {
					if (item === menuItem) {
						return;
					}

					if (isMobileNav() && item.classList.contains('hamburger-menu')) {
						return;
					}

					item.classList.remove('menu-item-expanded');
					setSubmenuMaxHeight(item.querySelector('.sub-menu'), false);
				});

				// Toggle current menu item
				if (isExpanded) {
					menuItem.classList.remove('menu-item-expanded');
					setSubmenuMaxHeight(nextSubmenu, false);
					setSubmenuHeight(null);
					if (menuItem.classList.contains('menu-item--search') && navSearchToggle) {
						navSearchToggle.setAttribute('aria-expanded', 'false');
					}
				} else {
					menuItem.classList.add('menu-item-expanded');
					nav.classList.add('nav-open');
					nav.classList.add('nav-opening');
					setTimeout(() => {
						nav.classList.remove('nav-opening');
					}, 600);
					if (menuItem.classList.contains('menu-item--search') && navSearchToggle) {
						navSearchToggle.setAttribute('aria-expanded', 'true');
					}
				}
				updateTabbability();
			});
		});

		// Close submenu when clicking outside
		document.addEventListener('click', (e) => {
			if (!nav.contains(e.target)) {
				closeNav();
			}
		});
		document.addEventListener('keydown', (e) => {
			if (e.key === 'Escape') {
				closeNav();
			}
		});

	const syncMenuState = () => {
		const expandedItem = nav.querySelector('.menu-item-expanded');
		const expandedSubmenu = expandedItem?.querySelector('.sub-menu');

		if (isMobileNav()) {
			setSubmenuHeight(expandedSubmenu);
			if (expandedSubmenu) {
				setSubmenuMaxHeight(expandedSubmenu, true);
			}
		} else {
			// Clear inline mobile-only styles when switching to desktop
			subMenus.forEach(submenu => {
				submenu.style.maxHeight = '';
				submenu.style.opacity = '';
			});
			setSubmenuHeight(expandedSubmenu);
		}
		updateTabbability();
	};

	window.addEventListener('resize', syncMenuState);
	syncMenuState();
	}

	updateTabbability();

	// Handle menu toggle button
	if (navToggle && nav) {
		navToggle.addEventListener('click', () => {
			toggleNav();
		});
	}

	if (navSearchToggle && searchMenuItem) {
		navSearchToggle.addEventListener('click', () => {
			toggleSearchMenu();
		});
	}

	if (searchClose) {
		searchClose.addEventListener('click', () => {
			closeSearchMenu();
			navSearchToggle?.focus();
		});
	}
}

